import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { TrophyIcon, ArrowUpIcon, ArrowDownIcon, MinusIcon } from '@heroicons/react/24/outline'
import { leaderboardAPI } from '../services/api'
import type { LeaderboardData, LeaderboardEntry } from '../types'
import LoadingScreen from '../components/LoadingScreen'
import Tooltip from '../components/Tooltip'
import { LEADERBOARD_GLOSSARY } from '../config/glossary'

// ---- Score breakdown tooltip ----
interface ScoreBreakdownProps {
  breakdown: Record<string, number>
}

const ScoreBreakdown = ({ breakdown }: ScoreBreakdownProps) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const entries = Object.entries(breakdown)

  if (entries.length === 0) return null

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        className="text-xs text-primary-600 underline decoration-dotted cursor-help"
      >
        Details
      </button>
      {showTooltip && (
        <div className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          {entries.map(([key, val]) => (
            <div key={key} className="flex justify-between gap-2">
              <span className="text-gray-300 capitalize">{key.replace(/_/g, ' ')}</span>
              <span className="font-medium">{typeof val === 'number' ? val.toFixed(2) : val}</span>
            </div>
          ))}
          <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  )
}

// ---- Rank change indicator ----
const RankChange = ({ change }: { change?: number }) => {
  if (change == null) return <MinusIcon className="h-4 w-4 text-gray-400" />
  if (change > 0) return (
    <span className="flex items-center text-success-600 text-xs font-medium">
      <ArrowUpIcon className="h-3 w-3" />+{change}
    </span>
  )
  if (change < 0) return (
    <span className="flex items-center text-danger-600 text-xs font-medium">
      <ArrowDownIcon className="h-3 w-3" />{change}
    </span>
  )
  return <MinusIcon className="h-4 w-4 text-gray-400" />
}

// ---- Entry Row ----
const EntryRow = ({ entry, index }: { entry: LeaderboardEntry; index: number }) => {
  const rankBg = index === 0
    ? 'bg-yellow-50 text-yellow-700'
    : index === 1
    ? 'bg-gray-100 text-gray-600'
    : index === 2
    ? 'bg-orange-50 text-orange-700'
    : 'bg-white text-gray-700'

  return (
    <tr className={`${index < 3 ? 'font-semibold' : ''} hover:bg-gray-50`}>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full text-sm font-bold ${rankBg}`}>
          {entry.rank}
        </span>
      </td>
      <td className="px-4 py-3">
        <RankChange change={entry.rank_change} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">
        Creator #{entry.creator_id}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
        {typeof entry.score === 'number' ? entry.score.toFixed(2) : entry.score}
      </td>
      <td className="px-4 py-3 text-sm">
        {entry.score_breakdown && Object.keys(entry.score_breakdown).length > 0 ? (
          <ScoreBreakdown breakdown={entry.score_breakdown} />
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {entry.reward_earned ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700">
            {entry.reward_earned}
          </span>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )}
      </td>
    </tr>
  )
}

// ---- Main Page ----
const Leaderboard = () => {
  const [leaderboards, setLeaderboards] = useState<LeaderboardData[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [currentBoard, setCurrentBoard] = useState<LeaderboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isBoardLoading, setIsBoardLoading] = useState(false)

  useEffect(() => {
    leaderboardAPI.list()
      .then((data: any) => {
        const list: LeaderboardData[] = Array.isArray(data) ? data : []
        setLeaderboards(list)
        if (list.length > 0) {
          setSelectedId(list[0].id)
        }
      })
      .catch(() => toast.error('Failed to load leaderboards'))
      .finally(() => setIsLoading(false))
  }, [])

  useEffect(() => {
    if (selectedId == null) return
    setIsBoardLoading(true)
    leaderboardAPI.get(selectedId, 20)
      .then((data: LeaderboardData) => setCurrentBoard(data))
      .catch(() => toast.error('Failed to load leaderboard entries'))
      .finally(() => setIsBoardLoading(false))
  }, [selectedId])

  if (isLoading) return <LoadingScreen />

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <TrophyIcon className="h-8 w-8 text-primary-600" />
        <h1 className="text-3xl font-bold text-gray-900">
          <Tooltip content={LEADERBOARD_GLOSSARY.ranking(currentBoard?.leaderboard_type)}>Creator Leaderboard</Tooltip>
        </h1>
      </div>

      {leaderboards.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <TrophyIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No leaderboards available yet.</p>
          <p className="text-gray-400 mt-1">Check back soon as creators build their track records.</p>
        </div>
      ) : (
        <>
          {/* Leaderboard selector tabs */}
          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200 px-4 overflow-x-auto">
              <div className="flex gap-1 min-w-max">
                {leaderboards.map((lb) => (
                  <button
                    key={lb.id}
                    type="button"
                    onClick={() => setSelectedId(lb.id)}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      selectedId === lb.id
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {lb.name || `${lb.leaderboard_type} — ${lb.period_label || lb.period}`}
                  </button>
                ))}
              </div>
            </div>

            {isBoardLoading ? (
              <div className="py-12 text-center text-gray-500">Loading entries...</div>
            ) : currentBoard ? (
              <>
                <div className="px-6 py-3 flex items-center justify-between border-b border-gray-100">
                  <div>
                    <span className="text-sm text-gray-500">
                      {currentBoard.period_label || currentBoard.period} &bull; {currentBoard.total_participants} participants
                    </span>
                  </div>
                </div>
                {currentBoard.entries.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    No entries in this leaderboard yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {['Rank', 'Change', 'Creator', 'Score', 'Breakdown', 'Reward'].map((h) => (
                            <th
                              key={h}
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {currentBoard.entries.map((entry, idx) => (
                          <EntryRow key={entry.creator_id} entry={entry} index={idx} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

export default Leaderboard
